import { NO_DATA, PATH_PROJECT_WITH_ID } from "@/constants";
import { useProjectQuery } from "@/modules/projects/hooks/useProjectQuery";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router-dom";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { ProjectMenu } from "./menu/ProjectMenu";
import { EntityLink } from "@/components/data-table/components/EntityLink";
import { DateRangeDisplayValue } from "@/components/DateRangeDisplayValue";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";

interface ProjectDetailsAndNavigationProps {
  projectId?: string;
  isOpen?: boolean;
  showEditButton?: boolean;
  showMenu?: boolean;
}

const ProjectDetailsAndNavigationComponent = ({
  projectId: projectIdFormProps,
}: ProjectDetailsAndNavigationProps) => {
  const { t } = useTranslation();
  const projectIdFromRoute = useProjectId();
  const projectId = projectIdFormProps || projectIdFromRoute;
  const query = useProjectQuery(projectId);

  const link = useMemo(() => {
    if (!projectId || query.isLoading) {
      return undefined;
    }
    return generatePath(PATH_PROJECT_WITH_ID, {
      projectId,
    });
  }, [projectId, query]);

  if (!projectId) {
    return null;
  }

  const {
    ref_number,
    name,
    client,
    start_date,
    end_date,
    primary_manager,
    status,
    project_type,
    last_confirmed_period,
  } = query.data ?? {};

  return (
    <section
      data-testid="project-details-bar"
      className="@container flex items-center space-x-1 rounded-md border bg-background p-1 shrink grow-0"
    >
      {projectId && (
        <>
          <ProjectMenu
            projectId={projectId}
            className="border-0 bg-transparent"
          />
          <Separator orientation="vertical" className="h-6" />
        </>
      )}

      <ul className="min-w-0 px-2 gap-x-5 gap-y-1 text-xs grid grid-rows-2 grid-flow-col @[850px]:text-sm @[700px]:flex">
        <li className="row-span-2 self-center">
          <EntityLink internalTo={link}>{ref_number}</EntityLink>
        </li>
        <li className="min-w-0 truncate font-bold">
          {name ? name : <Skeleton className="w-12 h-4" />}
        </li>
        <li className="min-w-0 truncate">
          {client ? client : <Skeleton className="w-12 h-4" />}
        </li>
        <li className="min-w-0 truncate font-bold">
          <DateRangeDisplayValue from={start_date} to={end_date} />
        </li>
        <li className="min-w-0 truncate">
          {primary_manager ? (
            primary_manager
          ) : (
            <Skeleton className="w-12 h-4" />
          )}
        </li>
        <li className="min-w-0 truncate font-bold">
          {project_type ? project_type : <Skeleton className="w-12 h-4" />}
        </li>
        <li className="min-w-0 truncate">
          {status ? (
            t(`projectStatuses.${status}`)
          ) : (
            <Skeleton className="w-12 h-4" />
          )}
        </li>
        {last_confirmed_period !== undefined && (
          <li
            className="min-w-0 truncate"
            title={t("projects.fields.last_confirmed_period")}
          >
            {last_confirmed_period ?? NO_DATA}
          </li>
        )}
      </ul>
    </section>
  );
};

export const ProjectDetailsAndNavigation = memo(
  ProjectDetailsAndNavigationComponent,
);
